/**
 * Cached fetch of raftor_webhq.catalog.api.get_swatch_color_overrides --
 * the explicit {attribute_value: hex} map that resolveSwatchColor()
 * (adapters/webshop.js) checks before any of its name-guessing heuristics.
 * See that endpoint's own docstring for why this exists: Item Attribute
 * Value has no color field at all, so without an explicit override two
 * differently-named colours can silently guess to the same swatch color,
 * or an unparseable name falls back to a flat grey with no way to fix it.
 *
 * One call, cached for the page's lifetime and shared by every source
 * adapter (webshop_source.js, recommendations_source.js,
 * wishlist_source.js) -- global and page-wide, not per-listing-fetch, so a
 * module-level cache (not a class field tied to one grid instance) is the
 * right scope; two different card surfaces on the same page (e.g. a grid
 * plus a "you may also like" carousel) must not each pay for their own
 * round trip.
 */

import { normalizeSwatchOverrides } from './adapters/webshop.js';

let cachedPromise = null;

function frappeCall(method) {
	return new Promise((resolve) => {
		if (typeof frappe === 'undefined' || !frappe.call) {
			resolve({});
			return;
		}
		let settled = false;
		const done = (value) => {
			if (settled) return;
			settled = true;
			resolve(value || {});
		};
		const req = frappe.call({ method, callback: (r) => done(r && r.message), error: () => done({}) });
		// Same deferred-net pattern as variant_resolver.js's own frappeCall:
		// `always` fires before `callback` on a genuine success, so it must be
		// deferred a tick or it would resolve {} over every real result.
		if (req && typeof req.always === 'function') {
			req.always(() => setTimeout(() => done({}), 0));
		}
	});
}

/** {lowercased attribute_value: hex}, ready to pass straight to
 * resolveSwatchColor() as its `overrides` argument. Never rejects -- a
 * failed/absent call resolves to {}, same "decorative, must never break
 * card rendering" contract every other optional decoration in
 * webshop_source.js's fetchDecorations already follows. */
export function getSwatchColorOverrides() {
	if (!cachedPromise) {
		cachedPromise = frappeCall('raftor_webhq.catalog.api.get_swatch_color_overrides').then(
			normalizeSwatchOverrides
		);
	}
	return cachedPromise;
}
